<template>
  <div class="container-fluid full-width-page-wrapper">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="font-weight-bold page-title mb-0">Review Technical Areas Keywords</h3>
      <div v-if="revision">
        <a
          v-if="isTbChairLevel"
          class="btn btn-outline-primary mr-2"
          :href="pdfLink"
          target="_blank"
          rel="noopener"
        >
          Print PDF
        </a>
        <b-button
          v-if="canDiscard"
          class="mr-2"
          variant="outline-danger"
          :disabled="busy"
          @click="discardRevision"
        >
          Discard
        </b-button>
        <b-button
          v-if="canRequestValidation"
          class="mr-2"
          variant="success"
          :disabled="busy"
          @click="requestValidation"
        >
          Ask for validation
        </b-button>
        <b-button
          v-if="revision.isValidated"
          variant="success"
          :disabled="busy"
          @click="applyRevision"
        >
          Apply changes
        </b-button>
      </div>
    </div>

    <b-alert v-if="forbidden" show variant="warning">
      You do not have permission to participate in keywords reviews.
    </b-alert>

    <ifac-loader v-else-if="busy && !revision && !canStartReview" />

    <div v-else-if="!revision && canStartReview">
      <p class="text-muted mb-3">
        Start a keywords-only review without changing committee structure.
      </p>
      <target-triennium-field v-model="targetTriennium" class="mb-3" />
      <b-button variant="primary" :disabled="busy" @click="startKeywordsReview">
        Start keywords review
      </b-button>
    </div>

    <div v-else-if="!revision">
      <b-alert show variant="info">
        No keywords review is currently in progress.
      </b-alert>
    </div>

    <div v-else>
      <target-triennium-field
        :target-triennium-number="revision.targetTrienniumNumber"
        readonly
      />

      <b-alert v-if="revision.status !== 'draft'" show variant="info">
        This keywords review is {{ revision.status.replace(/_/g, ' ') }}.
        <router-link to="/areas/validate">Open validation view</router-link>
      </b-alert>

      <b-alert v-else show variant="secondary">
        Current review level:
        <strong>{{ levelLabel(revision.keywordWorkflow?.currentLevel) }}</strong>
      </b-alert>

      <b-alert
        v-if="isTbChairLevel && !canEditTbReview"
        show
        variant="info"
      >
        The Technical Board Chair is reviewing keyword proposals. You can view the current proposals but cannot edit them.
      </b-alert>

      <b-alert
        v-if="keywordWarnings.length"
        show
        variant="warning"
      >
        Removing these keywords will affect affiliate profiles:
        <ul class="mb-0 mt-2">
          <li v-for="warning in keywordWarnings" :key="warning.keywordId">
            {{ warning.name }} ({{ warning.affiliateCount }} {{ warning.affiliateCount === 1 ? 'affiliate' : 'affiliates' }})
          </li>
        </ul>
      </b-alert>

      <ifac-loader v-if="busy" />

      <div v-show="!busy">
        <tb-keywords-review-table
          v-if="isTbChairLevel"
          ref="tbReview"
          :revision-id="revision.id"
          :snapshot="revision.snapshot"
          :can-edit="canEditTbReview"
          :similar-busy="similarKeywordsBusy"
          @see-similar="showSimilarKeywordsFromRow"
          @saved="onTbReviewSaved"
          @refresh-revision="refreshRevisionFromServer"
        />

        <template v-else>
          <b-card
            v-for="committee in activeCommittees"
            :key="committee.id"
            no-body
            class="mb-3"
          >
            <b-card-header>
              <div class="d-flex justify-content-between align-items-center">
                <strong>CC{{ committee.number }} — {{ committee.name }}</strong>
                <div>
                  <b-button
                    v-if="canSaveCc(committee.id)"
                    size="sm"
                    variant="primary"
                    class="mr-2"
                    :disabled="busy"
                    @click="saveCoordinatingCommittee(committee.id)"
                  >
                    Save CC review
                  </b-button>
                  <b-button
                    v-if="canSubmitCc(committee.id)"
                    size="sm"
                    variant="success"
                    :disabled="busy"
                    @click="submitCoordinatingCommittee(committee.id)"
                  >
                    Submit CC review
                  </b-button>
                </div>
              </div>
            </b-card-header>
            <b-card-body>
              <cc-keywords-review-table
                v-if="showCcReviewTable(committee.id)"
                :ref="ccReviewRef(committee.id)"
                :revision-id="revision.id"
                :coordinating-committee-id="committee.id"
                :similar-busy="similarKeywordsBusy"
                @see-similar="showSimilarKeywordsFromRow"
              />

              <template v-else>
                <b-card
                  v-for="technicalCommittee in activeTechnicalCommittees(committee)"
                  :key="technicalCommittee.id"
                  class="mb-3"
                >
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>{{ technicalCommitteeTitle(committee, technicalCommittee) }}</strong>
                    <div>
                      <b-badge v-if="isTcSubmitted(technicalCommittee.id)" variant="success" class="mr-2">
                        Submitted
                      </b-badge>
                      <b-button
                        v-if="canEditTc(technicalCommittee.id) && isTcChairLevel"
                        size="sm"
                        variant="primary"
                        class="mr-2"
                        :disabled="busy"
                        @click="saveTechnicalCommittee(committee, technicalCommittee)"
                      >
                        Save keywords
                      </b-button>
                      <b-button
                        v-if="canSubmitTc(technicalCommittee.id)"
                        size="sm"
                        variant="success"
                        class="mr-2"
                        :disabled="busy"
                        @click="submitTechnicalCommittee(technicalCommittee.id)"
                      >
                        Submit proposal
                      </b-button>
                      <b-button
                        v-if="canEditTc(technicalCommittee.id) && !isTcChairLevel"
                        size="sm"
                        variant="primary"
                        :disabled="busy"
                        @click="saveTechnicalCommittee(committee, technicalCommittee)"
                      >
                        Save keywords
                      </b-button>
                    </div>
                  </div>

                  <tc-keywords-proposal-table
                    v-if="canEditTc(technicalCommittee.id) && isTcChairLevel"
                    :committee="committee"
                    :technical-committee="technicalCommittee"
                    :baseline-snapshot="baselineSnapshot"
                    :similar-busy="similarKeywordsBusy"
                    @change="onTcKeywordsChange"
                    @see-similar="(row) => showSimilarKeywordsFromRow(row, committee, technicalCommittee)"
                  />

                  <ul v-else class="mb-0">
                    <li
                      v-for="keyword in activeKeywords(technicalCommittee)"
                      :key="keyword.id || keyword.draftId"
                    >
                      {{ keyword.name }}
                    </li>
                  </ul>
                </b-card>
              </template>
            </b-card-body>
          </b-card>
        </template>
      </div>
    </div>

    <similar-keywords-modal
      v-model="similarKeywordsModalVisible"
      :busy="similarKeywordsBusy"
      :content="similarKeywordsContent"
    />
  </div>
</template>

<script>
import { IfacLoader } from '@ifac/ui';
import Triennium from '@/helpers/triennium';
import Revisions from '@/services/Api/TechnicalAreas/Revisions';
import CcKeywordsReviewTable from './partials/CcKeywordsReviewTable.vue';
import SimilarKeywordsModal from './partials/SimilarKeywordsModal.vue';
import TargetTrienniumField from './TargetTrienniumField.vue';
import TbKeywordsReviewTable from './partials/TbKeywordsReviewTable.vue';
import TcKeywordsProposalTable from './partials/TcKeywordsProposalTable.vue';

export default {
  name: 'TechnicalAreasEditKeywords',
  components: {
    CcKeywordsReviewTable,
    IfacLoader,
    SimilarKeywordsModal,
    TargetTrienniumField,
    TbKeywordsReviewTable,
    TcKeywordsProposalTable,
  },
  data() {
    return {
      busy: true,
      forbidden: false,
      revision: null,
      keywordWarnings: [],
      canStartReview: false,
      targetTriennium: 'current',
      similarKeywordsModalVisible: false,
      similarKeywordsBusy: false,
      similarKeywordsContent: {
        exactMatches: [],
        similarMatches: [],
        withinTcDuplicate: false,
      },
    };
  },
  computed: {
    activeCommittees() {
      return (this.revision?.snapshot?.coordinatingCommittees || [])
        .filter((committee) => !committee.deleted);
    },
    permissions() {
      return this.revision?.keywordWorkflow || {};
    },
    canDiscard() {
      return this.permissions.canDiscard;
    },
    canRequestValidation() {
      return this.permissions.canRequestValidation;
    },
    canEditTbReview() {
      return Boolean(this.permissions.canUpdateFullSnapshot);
    },
    isTcChairLevel() {
      return this.permissions.currentLevel === 'tc_chair';
    },
    isCcChairLevel() {
      return this.permissions.currentLevel === 'cc_chair';
    },
    isTbChairLevel() {
      return this.permissions.currentLevel === 'tb_chair';
    },
    pdfLink() {
      if (!this.revision) {
        return '#';
      }

      const token = this.$store.state.auth?.tokens?.token;

      return Revisions.pdfUrl(this.revision.id, token);
    },
    baselineSnapshot() {
      return Revisions.baselineSnapshot(this.revision);
    },
    targetsNextTriennium() {
      if (!this.revision?.targetTrienniumNumber) {
        return false;
      }

      const current = this.$store.getters['settings/currentTriennium']
        ?? Triennium.calculateNumberFromYear();

      return this.revision.targetTrienniumNumber > current;
    },
  },
  async created() {
    await this.loadRevision();
  },
  methods: {
    levelLabel(level) {
      const labels = {
        tc_chair: 'Technical Committee Chairs',
        cc_chair: 'Coordinating Committee Chairs',
        tb_chair: 'Technical Board Chair',
      };

      return labels[level] || level;
    },
    activeTechnicalCommittees(committee) {
      return (committee.technicalCommittees || []).filter((tc) => !tc.deleted);
    },
    activeKeywords(technicalCommittee) {
      return (technicalCommittee.keywords || []).filter((keyword) => !keyword.deleted);
    },
    technicalCommitteeTitle(committee, technicalCommittee) {
      return `TC${committee.number}.${technicalCommittee.number} — ${technicalCommittee.name}`;
    },
    technicalCommitteeLabel(committee, technicalCommittee) {
      if (committee?.number === undefined || technicalCommittee?.number === undefined) {
        return null;
      }

      return `${committee.number}.${technicalCommittee.number}`;
    },
    isTcSubmitted(technicalCommitteeId) {
      return Boolean(this.revision?.workflow?.tcSubmissions?.[String(technicalCommitteeId)]);
    },
    includesTechnicalCommitteeId(list, technicalCommitteeId) {
      const id = Number(technicalCommitteeId);

      return (list || []).some((item) => Number(item) === id);
    },
    includesCoordinatingCommitteeId(list, coordinatingCommitteeId) {
      const id = Number(coordinatingCommitteeId);

      return (list || []).some((item) => Number(item) === id);
    },
    canEditTc(technicalCommitteeId) {
      return this.includesTechnicalCommitteeId(
        this.permissions.editableTechnicalCommitteeIds,
        technicalCommitteeId,
      );
    },
    canSubmitTc(technicalCommitteeId) {
      return this.includesTechnicalCommitteeId(
        this.permissions.submittableTechnicalCommitteeIds,
        technicalCommitteeId,
      );
    },
    canSubmitCc(coordinatingCommitteeId) {
      return this.includesCoordinatingCommitteeId(
        this.permissions.submittableCoordinatingCommitteeIds,
        coordinatingCommitteeId,
      );
    },
    canSaveCc(coordinatingCommitteeId) {
      return this.isCcChairLevel
        && this.includesCoordinatingCommitteeId(
          this.permissions.submittableCoordinatingCommitteeIds,
          coordinatingCommitteeId,
        );
    },
    showCcReviewTable(coordinatingCommitteeId) {
      if (!this.isCcChairLevel) {
        return false;
      }

      return this.includesCoordinatingCommitteeId(
        this.permissions.submittableCoordinatingCommitteeIds,
        coordinatingCommitteeId,
      );
    },
    ccReviewRef(coordinatingCommitteeId) {
      return `cc-review-${coordinatingCommitteeId}`;
    },
    getCcReviewComponent(coordinatingCommitteeId) {
      const ref = this.$refs[this.ccReviewRef(coordinatingCommitteeId)];

      return Array.isArray(ref) ? ref[0] : ref;
    },
    async loadRevision() {
      this.busy = true;
      this.forbidden = false;
      this.canStartReview = false;

      try {
        const permissionsResponse = await Revisions.permissions();
        const { canEditKeywords, canEditStructure } = permissionsResponse.data.data;

        if (!canEditKeywords) {
          this.forbidden = true;
          return;
        }

        const response = await Revisions.activeKeywords();
        this.revision = response.data.data;

        if (this.revision) {
          await this.refreshKeywordWarnings();
        } else {
          this.canStartReview = canEditStructure;
        }
      } catch (error) {
        if (error.response?.status === 403) {
          this.forbidden = true;
        } else {
          throw error;
        }
      } finally {
        this.busy = false;
      }
    },
    async startKeywordsReview() {
      this.busy = true;

      try {
        const response = await Revisions.create({
          type: 'keywords',
          targetTriennium: this.targetTriennium,
        });
        this.revision = response.data.data;
        this.canStartReview = false;
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not start keywords review.',
        });
      } finally {
        this.busy = false;
      }
    },
    async refreshKeywordWarnings() {
      if (!this.revision) {
        return;
      }

      const response = await Revisions.keywordRemovalImpact(this.revision.id);
      this.keywordWarnings = response.data.data.removedKeywords || [];
    },
    onTcKeywordsChange() {
      // Rows are synced directly on the technical committee object.
    },
    async saveTechnicalCommittee(committee, technicalCommittee, { quiet = false } = {}) {
      if (Revisions.findEmptyKeywordName(this.activeKeywords(technicalCommittee))) {
        this.$snack.danger({ text: Revisions.emptyKeywordSnack().text });

        return;
      }

      const duplicateName = Revisions.findWithinTcDuplicate(
        this.activeKeywords(technicalCommittee),
      );

      if (duplicateName) {
        const duplicateSnack = Revisions.withinTcDuplicateSnack({
          tcLabel: `${committee.number}.${technicalCommittee.number}`,
          name: duplicateName,
        });
        this.$snack.danger({ text: duplicateSnack.text });

        return;
      }

      this.busy = true;

      try {
        const response = await Revisions.updateTechnicalCommitteeKeywords(
          this.revision.id,
          technicalCommittee.id,
          this.activeKeywords(technicalCommittee),
        );
        this.revision = response.data.data;
        await this.refreshKeywordWarnings();

        if (!quiet) {
          this.$snack.success({ text: 'Keywords saved.' });
        }
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not save keywords.',
        });
      } finally {
        this.busy = false;
      }
    },
    async saveCoordinatingCommittee(coordinatingCommitteeId, { quiet = false } = {}) {
      const component = this.getCcReviewComponent(coordinatingCommitteeId);

      if (!component) {
        return;
      }

      this.busy = true;

      try {
        this.revision = await component.save();
        await this.refreshKeywordWarnings();

        if (!quiet) {
          this.$snack.success({ text: 'CC review saved.' });
        }
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not save CC review.',
        });
      } finally {
        this.busy = false;
      }
    },
    async submitTechnicalCommittee(technicalCommitteeId) {
      const committee = this.activeCommittees.find((cc) => this.activeTechnicalCommittees(cc)
        .some((tc) => tc.id === technicalCommitteeId));
      const technicalCommittee = committee
        ? this.activeTechnicalCommittees(committee).find((tc) => tc.id === technicalCommitteeId)
        : null;

      if (technicalCommittee && committee) {
        if (Revisions.findEmptyKeywordName(this.activeKeywords(technicalCommittee))) {
          this.$snack.danger({ text: Revisions.emptyKeywordSnack().text });

          return;
        }

        const duplicateName = Revisions.findWithinTcDuplicate(
          this.activeKeywords(technicalCommittee),
        );

        if (duplicateName) {
          const duplicateSnack = Revisions.withinTcDuplicateSnack({
            tcLabel: `${committee.number}.${technicalCommittee.number}`,
            name: duplicateName,
          });
          this.$snack.danger({ text: duplicateSnack.text });

          return;
        }

        await this.saveTechnicalCommittee(committee, technicalCommittee, { quiet: true });
      }

      this.busy = true;

      try {
        const response = await Revisions.submitTechnicalCommitteeKeywords(
          this.revision.id,
          technicalCommitteeId,
        );
        this.revision = response.data.data;
        this.$snack.success({ text: 'Technical committee proposal submitted.' });
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not submit proposal.',
        });
      } finally {
        this.busy = false;
      }
    },
    async submitCoordinatingCommittee(coordinatingCommitteeId) {
      const component = this.getCcReviewComponent(coordinatingCommitteeId);

      if (component) {
        await this.saveCoordinatingCommittee(coordinatingCommitteeId, { quiet: true });
      }

      this.busy = true;

      try {
        const response = await Revisions.submitCoordinatingCommitteeKeywords(
          this.revision.id,
          coordinatingCommitteeId,
        );
        this.revision = response.data.data;
        this.$snack.success({ text: 'Coordinating committee review submitted.' });
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not submit CC review.',
        });
      } finally {
        this.busy = false;
      }
    },
    onTbReviewSaved(revision) {
      this.revision = revision;
      this.refreshKeywordWarnings();
    },
    async refreshRevisionFromServer(revision = null) {
      if (revision) {
        this.revision = revision;

        return;
      }

      if (!this.revision?.id) {
        return;
      }

      const response = await Revisions.show(this.revision.id);
      this.revision = response.data.data;
    },
    technicalCommitteeLabelFromReviewRow(row) {
      const technicalCommitteeId = row.targetTcId || row.sourceTcId;

      if (!technicalCommitteeId) {
        return null;
      }

      const match = (this.revision?.snapshot?.coordinatingCommittees || [])
        .filter((committee) => !committee.deleted)
        .flatMap((committee) => (committee.technicalCommittees || [])
          .filter((technicalCommittee) => !technicalCommittee.deleted)
          .map((technicalCommittee) => ({ committee, technicalCommittee })))
        .find(({ technicalCommittee }) => Number(technicalCommittee.id) === Number(technicalCommitteeId));

      if (!match) {
        return null;
      }

      return this.technicalCommitteeLabel(match.committee, match.technicalCommittee);
    },
    async discardRevision() {
      const confirmed = await this.$bvModal.msgBoxConfirm('Discard this keywords review?');

      if (!confirmed) {
        return;
      }

      this.busy = true;

      try {
        await Revisions.discard(this.revision.id);
        this.revision = null;
        await this.loadRevision();
      } finally {
        this.busy = false;
      }
    },
    async requestValidation() {
      this.busy = true;
      await this.$nextTick();

      try {
        if (this.isTbChairLevel && this.$refs.tbReview) {
          const revision = await this.$refs.tbReview.saveReview({ quiet: true });

          if (!revision) {
            return;
          }

          this.revision = revision;
        }

        await Revisions.requestValidation(this.revision.id);
        this.$snack.success({ text: 'Validation requested.' });
        this.$router.push('/areas/validate');
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not request validation.',
        });
      } finally {
        this.busy = false;
      }
    },
    async applyRevision() {
      const message = this.targetsNextTriennium
        ? 'Apply validated keyword changes for the next triennium? They will be staged and the public Technical Areas page will not change until Admin switches the current triennium setting to that period.'
        : 'Apply validated keyword changes?';

      const confirmed = await this.$bvModal.msgBoxConfirm(message);

      if (!confirmed) {
        return;
      }

      this.busy = true;

      try {
        await Revisions.apply(this.revision.id);
        this.$snack.success({ text: 'Keywords applied.' });
        this.$router.push('/areas');
      } catch (error) {
        this.$snack.danger({
          text: error.response?.data?.error?.message || 'Could not apply keyword changes.',
        });
      } finally {
        this.busy = false;
      }
    },
    async showSimilarKeywordsFromRow(row, committee = null, technicalCommittee = null) {
      const name = this.isTbChairLevel
        ? (row.tbProposedName || row.ccProposedName || row.tcProposedName)
        : (row.ccProposedName || row.tbProposedName || row.tcProposedName);

      if (!name || !this.revision?.id) {
        return;
      }

      this.similarKeywordsModalVisible = true;
      this.similarKeywordsBusy = true;
      this.similarKeywordsContent = {
        exactMatches: [],
        similarMatches: [],
        withinTcDuplicate: false,
      };

      try {
        let technicalCommitteeLabel = row.tcLabel || null;

        if (!technicalCommitteeLabel && committee && technicalCommittee) {
          technicalCommitteeLabel = this.technicalCommitteeLabel(committee, technicalCommittee);
        }

        if (!technicalCommitteeLabel && this.isTbChairLevel) {
          technicalCommitteeLabel = this.technicalCommitteeLabelFromReviewRow(row);
        }

        const excludeKeywordIds = row.existingKeywordId ? [row.existingKeywordId] : [];
        // Omit client snapshot so the API loads the persisted revision snapshot.
        const response = await Revisions.checkKeywordSimilarity(name, {
          revisionId: this.revision.id,
          excludeKeywordIds,
          excludeDraftId: row.draftId,
          mode: 'informative',
          technicalCommitteeLabel,
        });

        this.similarKeywordsContent = Revisions.formatSimilarKeywordsModal(
          response.data.data,
          technicalCommitteeLabel,
        );
      } finally {
        this.similarKeywordsBusy = false;
      }
    },
  },
};
</script>
